/**
 * Matching Top Finder Module
 * Shown on Bottoms product pages. Lets shoppers pick a band + cup size and
 * returns in-stock Tops products in the currently selected bottom's color
 * that fit that bra size.
 *
 * Fit logic mirrors bra-size-selector.js's findMatchingVariantTops(): band+cup
 * resolves (via sizeMatrixTops) to a set of candidate cup_size groups, each
 * with a required numeric size. A Top matches when its product-level
 * cup_size metafield equals one of those groups AND it has an available
 * variant in that size and the selected color.
 *
 * sizeMatrixTops below is a duplicate of the one in bra-size-selector.js —
 * if that sizing matrix is ever updated, update it here too.
 */

class MatchingTopFinder {
  constructor(containerSelector, dataScriptSelector) {
    this.container = document.querySelector(containerSelector);
    if (!this.container) return;

    const dataScript = document.querySelector(dataScriptSelector);
    if (!dataScript) return;

    try {
      this.tops = JSON.parse(dataScript.textContent);
    } catch (e) {
      console.error('Error parsing matching tops data:', e);
      this.tops = [];
    }

    this.bandSizes = ['28', '30', '32', '34', '36', '38', '40', '42', '44', '46'];

    // Band size → cup size → cup type → variant info. Kept in sync with bra-size-selector.js.
    this.sizeMatrixTops = {
      '28': {
        'A': {},
        'B': { 'AB': { size: '30', fits: '30A/28B' } },
        'C': { 'ABC': { size: '30', fits: '32A/30B/28C' }, 'BC': { size: '30', fits: '30B/28C' } },
        'D': { 'CD': { size: '30', fits: '30C/28D' } },
        'DD(E)': { 'CDDD': { size: '30', fits: '32C/30D/28DD(E)' } },
        'DDD(F)': { 'DD/E/F': { size: '30', fits: '30DD(E)/28DDD(F)' }, 'D/E/F': { size: '30', fits: '32D/30DD(E)/28DDD(F)' } }
      },
      '30': {
        'A': { 'AB': { size: '30', fits: '30A/28B' } },
        'B': { 'ABCD': { size: '30', fits: '32A/30B/30C' }, 'AB': { size: '32', fits: '32A/30B' }, 'ABC': { size: '30', fits: '32A/30B/28C' }, 'BC': { size: '30', fits: '30B/28C' } },
        'C': { 'ABCD': { size: '30', fits: '32A/30B/30C' }, 'ABC': { size: '32', fits: '34A/32B/30C' }, 'BC': { size: '32', fits: '32B/30C' }, 'CD': { size: '30', fits: '30C/28D' } },
        'D': { 'ABCD': { size: '32', fits: '34A/32B/32C/30D/30DD(E)' }, 'BC': { size: '32', fits: '32C/30D' }, 'CD': { size: '30', fits: '32C/30D/28DD(E)' } },
        'DD(E)': { 'ABCD': { size: '32', fits: '34A/32B/32C/30D/30DD(E)' }, 'CDDD': { size: '32', fits: '34C/32D/30DD(E)' }, 'CDDD-pam': { size: '30', fits: '34C/32D/30DD(E)' }, 'D/DD': { size: '30', fits: '32D/30DD(E)' }, 'DD/E/F': { size: '30', fits: '30DD(E)/28DDD(F)' }, 'D/E/F': { size: '30', fits: '32D/30DD(E)/28DDD(F)' } },
        'DDD(F)': { 'DD/E/F': { size: '32', fits: '32DD(E)/30DDD(F)' }, 'D/E/F': { size: '32', fits: '34D/32DD(E)/30DDD(F)' } }
      },
      '32': {
        'A': { 'ABCD': { size: '30', fits: '32A/30B/30C' }, 'AB': { size: '32', fits: '32A/30B' }, 'ABC': { size: '30', fits: '32A/30B/28C' } },
        'B': { 'ABCD': { size: '32', fits: '34A/32B/32C/30D/30DD(E)' }, 'AB': { size: '34', fits: '34A/32B' }, 'ABC': { size: '32', fits: '34A/32B/30C' }, 'BC': { size: '32', fits: '32B/30C' } },
        'C': { 'ABCD': { size: '32', fits: '34A/32B/32C/30D/30DD(E)' }, 'ABC': { size: '34', fits: '36A/34B/32C' }, 'BC': { size: '34', fits: '34B/32C' }, 'CD': { size: '32', fits: '32C/30D' }, 'CDDD': { size: '30', fits: '32C/30D/28DD(E)' } },
        'D': { 'ABCD': { size: '34', fits: '36A/34B/34C/32D/32DD(E)' }, 'CD': { size: '34', fits: '34C/32D' }, 'CDDD': { size: '32', fits: '34C/32D/30DD(E)' }, 'CDDD-pam': { size: '30', fits: '34C/32D/30DD(E)' }, 'D/DD': { size: '30', fits: '32D/30DD(E)' }, 'D/E/F': { size: '30', fits: '32D/30DD(E)/28DDD(F)' } },
        'DD(E)': { 'ABCD': { size: '34', fits: '36A/34B/34C/32D/32DD(E)' }, 'CDDD': { size: '34', fits: '36C/34D/32DD(E)' }, 'CDDD-pam': { size: '32', fits: '36C/34D/32DD(E)' }, 'D/DD': { size: '32', fits: '34D/32DD(E)' }, 'DD/E/F': { size: '32', fits: '32DD(E)/30DDD(F)' }, 'D/E/F': { size: '32', fits: '34D/32DD(E)/30DDD(F)' } },
        'DDD(F)': { 'DD/E/F': { size: '34', fits: '34DD(E)/32DDD(F)' }, 'D/E/F': { size: '34', fits: '36D/34DD(E)/32DDD(F)' } }
      },
      '34': {
        'A': { 'ABCD': { size: '32', fits: '34A/32B/32C/30D/30DD(E)' }, 'AB': { size: '34', fits: '34A/32B' }, 'ABC': { size: '32', fits: '34A/32B/30C' } },
        'B': { 'ABCD': { size: '34', fits: '36A/34B/34C/32D/32DD(E)' }, 'AB': { size: '36', fits: '36A/34B' }, 'ABC': { size: '34', fits: '36A/34B/32C' }, 'BC': { size: '34', fits: '34B/32C' } },
        'C': { 'ABCD': { size: '34', fits: '36A/34B/34C/32D/32DD(E)' }, 'ABC': { size: '36', fits: '38A/36B/34C' }, 'BC': { size: '36', fits: '36B/34C' }, 'CD': { size: '34', fits: '34C/32D' }, 'CDDD': { size: '32', fits: '34C/32D/30DD(E)' }, 'CDDD-pam': { size: '30', fits: '34C/32D/30DD(E)' } },
        'D': { 'ABCD': { size: '36', fits: '38A/36B/36C/34D/34DD(E)' }, 'CD': { size: '36', fits: '36C/34D' }, 'CDDD': { size: '34', fits: '36C/34D/32DD(E)' }, 'CDDD-pam': { size: '32', fits: '36C/34D/32DD(E)' }, 'D/DD': { size: '32', fits: '34D/32DD(E)' }, 'D/E/F': { size: '32', fits: '34D/32DD(E)/30DDD(F)' } },
        'DD(E)': { 'ABCD': { size: '36', fits: '38A/36B/36C/34D/34DD(E)' }, 'CDDD': { size: '36', fits: '38C/36D/34DD(E)' }, 'CDDD-pam': { size: '34', fits: '38C/36D/34DD(E)' }, 'D/DD': { size: '34', fits: '36D/34DD(E)' }, 'DD/E/F': { size: '34', fits: '34DD(E)/32DDD(F)' }, 'D/E/F': { size: '34', fits: '36D/34DD(E)/32DDD(F)' } },
        'DDD(F)': { 'DD/E/F': { size: '36', fits: '36DD(E)/34DDD(F)' }, 'D/E/F': { size: '36', fits: '38D/36DD(E)/34DDD(F)' } }
      },
      '36': {
        'A': { 'ABCD': { size: '34', fits: '36A/34B/34C/32D/32DD(E)' }, 'AB': { size: '36', fits: '36A/34B' }, 'ABC': { size: '34', fits: '36A/34B/32C' } },
        'B': { 'ABCD': { size: '36', fits: '38A/36B/36C/34D/34DD(E)' }, 'AB': { size: '38', fits: '38A/36B' }, 'ABC': { size: '36', fits: '38A/36B/34C' }, 'BC': { size: '36', fits: '36B/34C' } },
        'C': { 'ABCD': { size: '36', fits: '38A/36B/36C/34D/34DD(E)' }, 'ABC': { size: '38', fits: '40A/38B/36C' }, 'BC': { size: '38', fits: '38B/36C' }, 'CD': { size: '36', fits: '36C/34D' }, 'CDDD': { size: '34', fits: '36C/34D/32DD(E)' }, 'CDDD-pam': { size: '32', fits: '36C/34D/32DD(E)' } },
        'D': { 'ABCD': { size: '38', fits: '40A/38B/38C/36D/36DD(E)' }, 'CD': { size: '38', fits: '38C/36D' }, 'CDDD': { size: '36', fits: '38C/36D/34DD(E)' }, 'CDDD-pam': { size: '34', fits: '38C/36D/34DD(E)' }, 'D/DD': { size: '34', fits: '36D/34DD(E)' }, 'DD/E/F': { size: '34', fits: '34DD(E)/32DDD(F)' }, 'D/E/F': { size: '34', fits: '36D/34DD(E)/32DDD(F)' } },
        'DD(E)': { 'ABCD': { size: '38', fits: '40A/38B/38C/36D/36DD(E)' }, 'CDDD': { size: '38', fits: '40C/38D/36DD(E)' }, 'CDDD-pam': { size: '36', fits: '40C/38D/36DD(E)' }, 'D/DD': { size: '36', fits: '38D/36DD(E)' }, 'DD/E/F': { size: '36', fits: '36DD(E)/34DDD(F)' }, 'D/E/F': { size: '36', fits: '38D/36DD(E)/34DDD(F)' } },
        'DDD(F)': { 'DD/E/F': { size: '38', fits: '38DD(E)/36DDD(F)' }, 'D/E/F': { size: '38', fits: '40D/38DD(E)/36DDD(F)' } }
      },
      '38': {
        'A': { 'ABCD': { size: '36', fits: '38A/36B/36C/34D/34DD(E)' }, 'AB': { size: '38', fits: '38A/36B' }, 'ABC': { size: '36', fits: '38A/36B/34C' } },
        'B': { 'ABCD': { size: '38', fits: '40A/38B/38C/36D/36DD(E)' }, 'ABC': { size: '38', fits: '40A/38B/36C' }, 'BC': { size: '38', fits: '38B/36C' } },
        'C': { 'ABCD': { size: '38', fits: '40A/38B/38C/36D/36DD(E)' }, 'CD': { size: '38', fits: '38C/36D' }, 'CDDD': { size: '36', fits: '38C/36D/34DD(E)' }, 'CDDD-pam': { size: '34', fits: '38C/36D/34DD(E)' } },
        'D': { 'CDDD': { size: '38', fits: '40C/38D/36DD(E)' }, 'CDDD-pam': { size: '36', fits: '40C/38D/36DD(E)' }, 'D/DD': { size: '36', fits: '38D/36DD(E)' }, 'DD/E/F': { size: '36', fits: '36DD(E)/34DDD(F)' }, 'D/E/F': { size: '36', fits: '38D/36DD(E)/34DDD(F)' } },
        'DD(E)': { 'CDDD': { size: '40', fits: '42C/40D/38DD(E)' }, 'CDDD-pam': { size: '38', fits: '42C/40D/38DD(E)' }, 'D/DD': { size: '38', fits: '40D/38DD(E)' }, 'DD/E/F': { size: '38', fits: '38DD(E)/36DDD(F)' }, 'D/E/F': { size: '38', fits: '40D/38DD(E)/36DDD(F)' } },
        'DDD(F)': { 'DD/E/F': { size: '40', fits: '40DD(E)/38DDD(F)' }, 'D/E/F': { size: '40', fits: '42D/40DD(E)/38DDD(F)' } }
      },
      '40': {
        'A': { 'ABCD': { size: '38', fits: '40A/38B/38C/36D/36DD(E)' }, 'ABC': { size: '38', fits: '40A/38B/36C' } },
        'B': {},
        'C': { 'CD': { size: '38', fits: '40C/38D/36DD(E)' }, 'CDDD-pam': { size: '36', fits: '40C/38D/36DD(E)' } },
        'D': { 'CDDD': { size: '40', fits: '42C/40D/38DD(E)' }, 'CDDD-pam': { size: '38', fits: '42C/40D/38DD(E)' }, 'D/DD': { size: '38', fits: '40D/38DD(E)' }, 'D/E/F': { size: '38', fits: '40D/38DD(E)/36DDD(F)' } },
        'DD(E)': { 'CDDD': { size: '42', fits: '44C/42D/40DD(E)' }, 'CDDD-pam': { size: '40', fits: '44C/42D/40DD(E)' }, 'D/DD': { size: '40', fits: '42D/40DD(E)' }, 'DD/E/F': { size: '40', fits: '40DD(E)/38DDD(F)' }, 'D/E/F': { size: '40', fits: '42D/40DD(E)/38DDD(F)' } },
        'DDD(F)': { 'DD/E/F': { size: '42', fits: '42DD(E)/40DDD(F)' }, 'D/E/F': { size: '42', fits: '44D/42DD(E)/40DDD(F)' } }
      },
      '42': {
        'A': {},
        'B': {},
        'C': { 'CDDD': { size: '40', fits: '42C/40D/38DD(E)' }, 'CDDD-pam': { size: '38', fits: '42C/40D/38DD(E)' } },
        'D': { 'CDDD': { size: '42', fits: '44C/42D/40DD(E)' }, 'CDDD-pam': { size: '40', fits: '44C/42D/40DD(E)' }, 'D/DD': { size: '40', fits: '42D/40DD(E)' }, 'D/E/F': { size: '40', fits: '42D/40DD(E)/38DDD(F)' } },
        'DD(E)': { 'CDDD': { size: '44', fits: '46C/44D/42DD(E)' }, 'D/DD': { size: '42', fits: '44D/42DD(E)' }, 'DD/E/F': { size: '42', fits: '42DD(E)/40DDD(F)' }, 'D/E/F': { size: '42', fits: '44D/42DD(E)/40DDD(F)' } },
        'DDD(F)': { 'DD/E/F': { size: '44', fits: '44DD(E)/42DDD(F)' }, 'D/E/F': { size: '44', fits: '46D/44DD(E)/42DDD(F)' } }
      },
      '44': {
        'A': {},
        'B': {},
        'C': { 'CDDD': { size: '42', fits: '44C/42D/40DD(E)' }, 'CDDD-pam': { size: '40', fits: '44C/42D/40DD(E)' } },
        'D': { 'CDDD': { size: '44', fits: '46C/44D/42DD(E)' }, 'D/E/F': { size: '42', fits: '44D/42DD(E)/40DDD(F)' } },
        'DD(E)': { 'D/DD': { size: '44', fits: '46D/44DD(E)' }, 'DD/E/F': { size: '44', fits: '44DD(E)/42DDD(F)' }, 'D/E/F': { size: '44', fits: '46D/44DD(E)/42DDD(F)' } },
        'DDD(F)': {}
      },
      '46': {
        'A': {},
        'B': {},
        'C': { 'CDDD': { size: '44', fits: '46C/44D/42DD(E)' } },
        'D': { 'D/DD': { size: '44', fits: '46D/44DD(E)' }, 'D/E/F': { size: '44', fits: '46D/44DD(E)/42DDD(F)' } },
        'DD(E)': {},
        'DDD(F)': {}
      }
    };

    this.toggleButton = null;
    this.toggleContent = null;
    this.bandSizeDropdown = null;
    this.cupSizeDropdown = null;
    this.resultsContainer = null;

    this.validCupSizes = [];
    this.heightResetTimer = null;
    this.scrollPinTimer = null;

    this.init();
  }

  init() {
    this.render();
    this.setupEventListeners();
  }

  render() {
    const bandOptionsHtml = this.bandSizes
      .map(size => `<option value="${size}">${size}</option>`)
      .join('');

    const html = `
      <div class="matching-top-finder">
        <button class="matching-top-finder__toggle" type="button">
          <span>FIND A MATCHING TOP</span>
          <span class="matching-top-finder__toggle-icon">▼</span>
        </button>
        <div class="matching-top-finder__content">
          <div class="matching-top-finder__content-inner">
            <div class="matching-finder-row">
              <div class="matching-finder-group">
                <label for="matching-band-size">BAND SIZE</label>
                <div class="matching-finder-inline">
                  <div class="matching-finder-select-wrap">
                    <select id="matching-band-size" class="matching-band-size-dropdown">
                      <option value="">—</option>
                      ${bandOptionsHtml}
                    </select>
                  </div>
                </div>
              </div>
              <div class="matching-finder-group">
                <label for="matching-cup-size">CUP SIZE</label>
                <div class="matching-finder-inline">
                  <div class="matching-finder-select-wrap">
                    <select id="matching-cup-size" class="matching-cup-size-dropdown" disabled>
                      <option value="">—</option>
                    </select>
                  </div>
                  <p class="matching-finder-empty"></p>
                </div>
              </div>
            </div>
            <div class="matching-finder-results"></div>
          </div>
        </div>
      </div>
    `;

    this.container.innerHTML = html;

    this.toggleButton = this.container.querySelector('.matching-top-finder__toggle');
    this.toggleContent = this.container.querySelector('.matching-top-finder__content');
    this.bandSizeDropdown = this.container.querySelector('.matching-band-size-dropdown');
    this.cupSizeDropdown = this.container.querySelector('.matching-cup-size-dropdown');
    this.emptyMessage = this.container.querySelector('.matching-finder-empty');
    this.resultsContainer = this.container.querySelector('.matching-finder-results');
  }

  setupEventListeners() {
    this.toggleButton.addEventListener('click', () => this.toggleCollapsible());
    this.bandSizeDropdown.addEventListener('change', () => this.handleBandSizeChange());
    this.cupSizeDropdown.addEventListener('change', () => this.handleCupSizeChange());

    // Re-filter automatically if the shopper changes the bottom's color after picking a size
    document.querySelectorAll('input[type="radio"][data-index="option1"]').forEach(input => {
      input.addEventListener('change', () => {
        if (this.bandSizeDropdown.value && this.cupSizeDropdown.value) this.handleCupSizeChange();
      });
    });

    this.resultsContainer.addEventListener('click', (e) => {
      const button = e.target.closest('.matching-finder-add-to-cart');
      if (button) this.handleAddToCart(button);
    });
  }

  toggleCollapsible() {
    const scrollY = window.scrollY;

    this.toggleButton.classList.toggle('is-open');
    this.toggleContent.classList.toggle('is-open');

    // Some browsers try to scroll the toggle/its expanding content into
    // view once the layout changes — pin the scroll position so the page
    // stays put and the revealed content just extends past the viewport.
    window.scrollTo(window.scrollX, scrollY);
    requestAnimationFrame(() => window.scrollTo(window.scrollX, scrollY));
    clearTimeout(this.scrollPinTimer);
    this.scrollPinTimer = setTimeout(() => window.scrollTo(window.scrollX, scrollY), 320);
  }

  handleBandSizeChange() {
    const bandSize = this.bandSizeDropdown.value;

    this.cupSizeDropdown.value = '';
    this.cupSizeDropdown.innerHTML = '<option value="">—</option>';
    this.cupSizeDropdown.disabled = !bandSize;

    this.emptyMessage.innerHTML = '';
    this.setResultsHtml('');
    this.validCupSizes = [];

    if (!bandSize) return;

    this.validCupSizes = Object.keys(this.sizeMatrixTops[bandSize]).filter(
      cupSize => Object.keys(this.sizeMatrixTops[bandSize][cupSize]).length > 0
    );

    this.validCupSizes.forEach(cupSize => {
      const option = document.createElement('option');
      option.value = cupSize;
      option.textContent = cupSize;
      this.cupSizeDropdown.appendChild(option);
    });
  }

  handleCupSizeChange() {
    const bandSize = this.bandSizeDropdown.value;
    const cupSize = this.cupSizeDropdown.value;

    this.emptyMessage.innerHTML = '';

    if (!bandSize || !cupSize) {
      this.setResultsHtml('');
      return;
    }

    const selectedColor = this.getSelectedColor();

    if (!selectedColor) {
      this.emptyMessage.textContent = 'Select a color above first.';
      this.setResultsHtml('');
      return;
    }

    const matches = this.findMatches(selectedColor, bandSize, cupSize);

    if (matches.length) {
      this.setResultsHtml(this.buildResultsHtml(matches));
    } else {
      this.showEmpty();
      this.setResultsHtml('');
    }
  }

  // Swaps in new results HTML but animates the container's height from its
  // old size to its new one, instead of snapping straight there — so
  // switching between sizes with different match counts doesn't visually
  // "hop" the rest of the page up or down.
  setResultsHtml(html) {
    const el = this.resultsContainer;
    const oldHeight = el.getBoundingClientRect().height;

    clearTimeout(this.heightResetTimer);
    el.style.transition = '';
    el.style.overflow = 'hidden';
    el.style.height = oldHeight + 'px';

    el.innerHTML = html;

    const newHeight = el.scrollHeight;

    requestAnimationFrame(() => {
      el.style.transition = 'height 0.25s ease';
      el.style.height = newHeight + 'px';
    });

    this.heightResetTimer = setTimeout(() => {
      el.style.transition = '';
      el.style.height = '';
      el.style.overflow = '';
    }, 260);
  }

  getSelectedColor() {
    const colorInput = document.querySelector('input[type="radio"][data-index="option1"]:checked');
    if (colorInput) return colorInput.value.toLowerCase();

    const colorSelect = document.querySelector('.product-form__input--swatch input:checked, .variant-input--color input:checked');
    if (colorSelect) return colorSelect.value.toLowerCase();

    return null;
  }

  findMatches(selectedColor, bandSize, cupSize) {
    const cupTypeOptions = this.sizeMatrixTops[bandSize][cupSize] || {};
    const matches = [];

    this.tops.forEach(top => {
      const topCupSize = (top.cup_size || '').trim();

      for (const [cupType, variantInfo] of Object.entries(cupTypeOptions)) {
        if (topCupSize !== cupType) continue;

        const variant = (top.variants || []).find(v => {
          return v.available
            && (v.color || '').toLowerCase().trim() === selectedColor
            && (v.size || '').toString().trim() === variantInfo.size;
        });

        if (variant) {
          matches.push({ product: top, variant, fits: variantInfo.fits });
          break;
        }
      }
    });

    return matches;
  }

  buildResultsHtml(matches) {
    return matches.map(({ product, variant, fits }) => `
      <div class="matching-finder-result-card">
        <a href="${product.url}?variant=${variant.id}">
          <img src="${variant.image}" alt="${this.escapeHtml(product.title)}" loading="lazy">
        </a>
        <a class="matching-finder-result-card__title" href="${product.url}?variant=${variant.id}">${this.escapeHtml(product.title)} • ${this.escapeHtml(variant.size)}</a>
        <p class="matching-finder-result-card__fits">FITS ${this.escapeHtml(fits)}</p>
        <p class="matching-finder-result-card__price">${variant.price}</p>
        <button type="button" class="matching-finder-add-to-cart" data-variant-id="${variant.id}" data-size="${this.escapeHtml(variant.size)}" data-fits="${this.escapeHtml(fits)}">ADD TO CART</button>
      </div>
    `).join('');
  }

  showEmpty() {
    this.emptyMessage.innerHTML = 'No matches in-stock, try a <a href="https://www.maliamills.com/collections/swimwear-bikini-tops?filter.v.availability=1">mixer</a>!';
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  handleAddToCart(button) {
    if (button.disabled) return;

    const variantId = button.dataset.variantId;
    const size = button.dataset.size;
    const fits = button.dataset.fits;
    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'ADDING...';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{
          id: variantId,
          quantity: 1,
          // Underscore-prefixed properties are hidden from the cart, checkout,
          // and order confirmation, but still show on the order in admin —
          // this is purely for tracking whether shoppers use this tool.
          properties: {
            '_Added via': 'Matching Top Finder',
            '_Matched size': size,
            '_Fits': fits
          }
        }]
      })
    })
      .then(response => {
        if (!response.ok) throw new Error('Add to cart failed');
        return response.json();
      })
      .then(() => {
        button.textContent = 'ADDED!';
        button.classList.add('is-added');
        setTimeout(() => {
          button.textContent = originalText;
          button.classList.remove('is-added');
          button.disabled = false;
        }, 2000);
      })
      .catch(err => {
        console.error('Error adding to cart:', err);
        button.textContent = 'ERROR — TRY AGAIN';
        button.disabled = false;
      });
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  new MatchingTopFinder('.matching-top-finder-container', '#MatchingTopsData');
});
